import { useEffect, ReactElement, useState } from "react"
import BaseTable, { AutoResizer, Column } from "react-base-table"
import Select, { SingleValue } from "react-select"
import { FormLabel, Modal, TextInput } from "@wfp/ui"
import { faPlus, faTrash, faPen } from "@fortawesome/free-solid-svg-icons"
import {
  useLazyGetAllCategoriesQuery,
  useAddCategoryMutation,
  useDeleteCategoryMutation,
  useEditCategoryMutation,
} from "services/household/categories"
import {
  useLazyGetSubCategoriesQuery,
  useAddSubCategoryMutation,
  useDeleteSubCategoryMutation,
  useEditSubCategoryMutation,
} from "services/household/subCategories"
import {
  useLazyGetAttributesQuery,
  useAddAttributeMutation,
  useDeleteAttributeMutation,
  useEditAttributeMutation,
} from "services/household/attributes"
import {
  useLazyGetModalitiesQuery,
  useAddModalityMutation,
  useDeleteModalityMutation,
  useEditModalityMutation,
} from "services/household/modalities"
import {
  useAddOrganizationMutation,
  useLazyGetOrganizationsQuery,
  useEditOrganizationMutation,
  useDeleteOrganizationMutation,
} from "services/household/organizations"
import useCommon from "hooks/useCommon"
import { CommonGetOptions } from "interfaces/dataInterfaces"
import { permissionsHH } from "constants/household"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"
import Button from "components/Shared/Button/Button"

type Props = {
  type: string
  withFilter?: boolean
}

const initialAction = { rowData: null, type: "", newData: "", filterId: 0 }

const Settings = ({ type, withFilter }: Props): ReactElement => {
  const { showSnackBar } = useCommon()
  const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1)

  const [filters, setFilters] = useState<{ categories: CommonGetOptions[] }>({ categories: [] })
  const [currentCategory, setCurrentCategory] = useState<CommonGetOptions | null>(null)
  const [data, setData] = useState<CommonGetOptions[]>([])
  const [action, setAction] = useState<{
    rowData: CommonGetOptions | null
    type: string
    newData?: string
    filterId?: number
  }>(initialAction)

  // Get all
  const [getCategories, categories] = useLazyGetAllCategoriesQuery()
  const [getSubCategories, subCategories] = useLazyGetSubCategoriesQuery()
  const [getModalities, modalities] = useLazyGetModalitiesQuery()
  const [getAttributes, attributes] = useLazyGetAttributesQuery()
  const [getOrganizations, organizations] = useLazyGetOrganizationsQuery()

  // Add
  const [addCategory] = useAddCategoryMutation()
  const [addSubCategory] = useAddSubCategoryMutation()
  const [addModality] = useAddModalityMutation()
  const [addAttribute] = useAddAttributeMutation()
  const [addOrganization] = useAddOrganizationMutation()

  // Edit
  const [editCategory] = useEditCategoryMutation()
  const [editSubCategory] = useEditSubCategoryMutation()
  const [editModality] = useEditModalityMutation()
  const [editAttribute] = useEditAttributeMutation()
  const [editOrganization] = useEditOrganizationMutation()

  // Delete
  const [deleteCategory] = useDeleteCategoryMutation()
  const [deleteSubCategory] = useDeleteSubCategoryMutation()
  const [deleteModality] = useDeleteModalityMutation()
  const [deleteAttribute] = useDeleteAttributeMutation()
  const [deleteOrganization] = useDeleteOrganizationMutation()

  const columns = [
    {
      key: "name",
      title: capitalizedType,
      dataKey: "name",
      width: 600,
      flexGrow: 1,
    },
    {
      key: "action",
      title: "",
      width: 250,
      align: Column.Alignment.RIGHT,
      cellRenderer: ({ rowData }) => (
        <>
          <PermissionWrapper {...permissionsHH} feature="edit">
            <Button
              action={() => setAction({ rowData, type: "edit", filterId: rowData?.categoryId, newData: rowData?.name })}
              label="Edit"
              className="btn-full primary action-btn"
              icon={faPen}
            />
          </PermissionWrapper>

          <PermissionWrapper {...permissionsHH} feature="delete">
            <Button
              action={() => setAction({ rowData, type: "delete" })}
              label="Delete"
              className="btn-full danger action-btn ml-1"
              icon={faTrash}
            />
          </PermissionWrapper>
        </>
      ),
    },
  ]

  const fetchListData = async (): Promise<void> => {
    switch (type) {
      case "category": {
        await getCategories().unwrap()
        break
      }
      case "type": {
        const categoryList = await getCategories().unwrap()
        setFilters({ categories: categoryList })
        break
      }
      case "modality": {
        await getModalities().unwrap()
        break
      }
      case "attribute": {
        await getAttributes().unwrap()
        break
      }
      case "organization": {
        await getOrganizations().unwrap()
        break
      }
      default:
        break
    }
  }

  const handleTypeChange = async (opt: SingleValue<CommonGetOptions>): Promise<void> => {
    try {
      if (opt) {
        setCurrentCategory(opt)
        await getSubCategories({ categoryId: Number(opt.value) }).unwrap()
      } else {
        setData([])
      }
    } catch (error) {
      showSnackBar("An error occurred while processing your request", "error")
    }
  }

  const handleAdd = async (): Promise<boolean> => {
    if (!action.filterId && type === "type") {
      showSnackBar("Category is required", "warning")
      return false
    }
    if (!action.newData) {
      showSnackBar("Name is required", "warning")
      return false
    }
    switch (type) {
      case "category": {
        await addCategory({ name: action.newData }).unwrap()
        break
      }
      case "type": {
        action.filterId && (await addSubCategory({ name: action.newData, categoryId: action.filterId }).unwrap())
        break
      }
      case "modality": {
        await addModality({ name: action.newData }).unwrap()
        break
      }
      case "attribute": {
        await addAttribute({ name: action.newData }).unwrap()
        break
      }
      case "organization": {
        await addOrganization({ name: action.newData }).unwrap()
        break
      }
      default:
        break
    }
    return true
  }

  const handleEdit = async (): Promise<boolean> => {
    if (!action.newData) {
      showSnackBar("Name is required", "warning")
      return false
    }
    switch (type) {
      case "category": {
        await editCategory({ id: Number(action.rowData?.value), name: action.newData }).unwrap()
        break
      }
      case "type": {
        action.filterId &&
          (await editSubCategory({
            id: Number(action.rowData?.value),
            name: action.newData,
            categoryId: action.filterId,
          }).unwrap())
        break
      }
      case "modality": {
        await editModality({ id: Number(action.rowData?.value), name: action.newData }).unwrap()
        break
      }
      case "attribute": {
        await editAttribute({ id: Number(action.rowData?.value), name: action.newData }).unwrap()
        break
      }
      case "organization": {
        await editOrganization({ id: Number(action.rowData?.value), name: action.newData }).unwrap()
        break
      }
      default:
        break
    }
    return true
  }

  const handleDelete = async (): Promise<void> => {
    switch (type) {
      case "category": {
        await deleteCategory({ id: Number(action.rowData?.value) }).unwrap()
        break
      }
      case "type": {
        await deleteSubCategory({ id: Number(action.rowData?.value) }).unwrap()
        break
      }
      case "modality": {
        await deleteModality({ id: Number(action.rowData?.value) }).unwrap()
        break
      }
      case "attribute": {
        await deleteAttribute({ id: Number(action.rowData?.value) }).unwrap()
        break
      }
      case "organization": {
        await deleteOrganization({ id: Number(action.rowData?.value) }).unwrap()
        break
      }
      default:
        break
    }
  }

  const handleSubmit = async (): Promise<void> => {
    switch (action.type) {
      case "add": {
        try {
          const res = await handleAdd()
          if (!res) return
          showSnackBar(`${capitalizedType} added successfully`, "success")
        } catch (error) {
          showSnackBar("An error occurred while processing your request", "error")
        }

        break
      }
      case "edit": {
        try {
          const res = await handleEdit()
          if (!res) return
          showSnackBar(`${capitalizedType} updated successfully`, "success")
        } catch (error) {
          showSnackBar("An error occurred while processing your request", "error")
        }
        break
      }
      case "delete": {
        try {
          await handleDelete()
          showSnackBar(`${capitalizedType} deleted successfully`, "success")
        } catch (error) {
          showSnackBar("An error occurred while processing your request", "error")
        }
        break
      }
      default:
        break
    }

    setAction(initialAction)
  }

  useEffect(() => {
    ;(async () => {
      try {
        await fetchListData()
      } catch (error) {
        showSnackBar("An error occurred while processing your request", "error")
      }
    })()
  }, [type])

  useEffect(() => {
    switch (type) {
      case "category": {
        categories?.data && setData(categories.data)
        break
      }
      case "type": {
        subCategories?.data &&
          setData(subCategories.data.map(sub => ({ ...sub, categoryId: Number(currentCategory?.value) })))
        categories?.data && setFilters({ categories: categories?.data })
        !categories?.data?.some(cat => cat.value === currentCategory?.value) && setData([])
        break
      }
      case "modality": {
        modalities?.data && setData(modalities.data)
        break
      }
      case "attribute": {
        attributes?.data && setData(attributes.data)
        break
      }
      case "organization": {
        organizations?.data && setData(organizations.data)
        break
      }
      default:
        break
    }
  }, [categories?.data, subCategories?.data, modalities?.data, attributes?.data, organizations?.data])

  return (
    <div>
      <div className={`flex ${withFilter ? "justify-between" : "justify-end"}`}>
        {withFilter && (
          <Select
            className="w-[400px]"
            placeholder="Select category"
            options={filters.categories ?? []}
            onChange={handleTypeChange}
            value={filters.categories?.find(cat => cat.value === currentCategory?.value) ?? null}
            getOptionLabel={opt => opt.name}
          />
        )}

        <PermissionWrapper {...permissionsHH} feature="addNew">
          <Button
            label={`Add ${type}`}
            className="btn-full green"
            icon={faPlus}
            action={() => setAction({ rowData: null, type: "add" })}
          />
        </PermissionWrapper>
      </div>

      <div style={{ height: "calc(100vh - 240px)", minHeight: "60vh" }} className="mt-2">
        <AutoResizer>
          {({ width, height }) => (
            <BaseTable
              data={data}
              width={width}
              height={height}
              rowKey="value"
              className="custom-table"
              style={{ fontSize: "0.875rem" }}
              rowHeight={50}
              headerHeight={45}
              columns={columns}
              emptyRenderer={() => <p className="my-2 text-center">No records to display</p>}
            />
          )}
        </AutoResizer>
      </div>

      <Modal
        lazyLoad
        open={!!action.type}
        primaryButtonText="Confirm"
        onRequestSubmit={() => handleSubmit()}
        secondaryButtonText="Cancel"
        onRequestClose={() => setAction(initialAction)}
        modalHeading={`${action.type.charAt(0).toUpperCase() + action.type.slice(1)} ${type}`}
        danger={action.type === "delete"}
      >
        {action.type !== "delete" ? (
          <div>
            {withFilter && (
              <div className="mb-2">
                <FormLabel>Category</FormLabel>
                {action.type === "add" ? (
                  <Select
                    className="w-[400px]"
                    placeholder="Select category"
                    options={filters.categories ?? []}
                    value={filters.categories?.find(cat => cat.value === action.filterId?.toString())}
                    onChange={opt => setAction(prev => ({ ...prev, filterId: Number(opt?.value) }))}
                    getOptionLabel={opt => opt.name}
                  />
                ) : (
                  <p>{currentCategory?.name}</p>
                )}
              </div>
            )}
            <TextInput
              labelText="Name"
              defaultValue={action.rowData?.name}
              onChange={e => setAction(prev => ({ ...prev, newData: e.target.value }))}
            />
          </div>
        ) : (
          <p>
            Are you sure you want to delete <b className="italic">{action.rowData?.name}</b>?
          </p>
        )}
      </Modal>
    </div>
  )
}

export default Settings
